import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ls } from '@softion/tools';
import { AdminUser, LoginResponse } from '../models/auth.models';
import { environment } from '../../../environments/environment';
import { CsrfService } from './csrf.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly csrf = inject(CsrfService);
  private readonly ACCESS_KEY  = 'sof_mgmt_access';
  private readonly REFRESH_KEY = 'sof_mgmt_refresh';
  private readonly USER_KEY    = 'sof_mgmt_user';

  readonly accessToken  = signal<string | null>(ls.get(this.ACCESS_KEY) as string | null);
  readonly refreshToken = signal<string | null>(ls.get(this.REFRESH_KEY) as string | null);
  readonly user         = signal<AdminUser | null>(ls.get(this.USER_KEY) as AdminUser | null);
  readonly isLoggedIn = computed(() => !!this.accessToken());

  login(tokens: LoginResponse, user: AdminUser): void {
    ls.set(this.ACCESS_KEY,  tokens.accessToken);
    ls.set(this.REFRESH_KEY, tokens.refreshToken);
    ls.set(this.USER_KEY,    user);
    this.accessToken.set(tokens.accessToken);
    this.refreshToken.set(tokens.refreshToken);
    this.user.set(user);
  }

  logout(): void {
    ls.remove(this.ACCESS_KEY);
    ls.remove(this.REFRESH_KEY);
    ls.remove(this.USER_KEY);
    this.accessToken.set(null);
    this.refreshToken.set(null);
    this.user.set(null);
    this.csrf.clear();
  }

  async logoutAsync(): Promise<void> {
    const refreshToken = this.refreshToken();
    try {
      if (refreshToken) {
        await firstValueFrom(
          this.http.post(`${environment.apiUrl}/auth/logout`, { refreshToken }),
        );
      }
    } finally {
      this.logout();
    }
  }

  getToken(): string | null {
    return this.accessToken();
  }

  getRefreshToken(): string | null {
    return this.refreshToken();
  }

  setAccessToken(token: string): void {
    ls.set(this.ACCESS_KEY, token);
    this.accessToken.set(token);
  }

  setRefreshToken(token: string): void {
    ls.set(this.REFRESH_KEY, token);
    this.refreshToken.set(token);
  }
}
